import { memo, useCallback, useContext, useMemo, useState } from 'react';
import type { CalendarProps, TileDisabledFunc } from 'react-calendar';
import { Calendar } from 'react-calendar';
import { FormattedMessage, useIntl } from 'react-intl';
import './CalendarStyles.css';
import type { TileArgs, Value } from 'react-calendar/src/shared/types';
import { DateTime } from 'luxon';
import BaseInput from '../text/BaseInput';
import isTileDisabledForDate from '../../../util/date/isTileDisabledForDate';
import type { BaseInputProps } from '../text/BaseInput';
import { SidePane, TooltipStateLess } from '../../popup';
import { ChevronLeftIcon, ChevronRightIcon, CloseIcon } from '../../icons';
import { HStack } from '../../layout';
import { Button } from '../../buttons';
import LayoutContext from '../../../contexts/LayoutContext';
import styles from './DateInput.module.css';

export interface DateInputProps
    extends Omit<BaseInputProps, 'value' | 'onChange'> {
    /**
     * Current value of the date input.
     */
    value?: Date;
    /**
     * A list of valid dates. Any dates that are not present in this array will be disabled.
     */
    validDates?: string[];
    /**
     * Props that are applied to the calendar component.
     */
    calendarProps?: Pick<
        CalendarProps,
        'minDate' | 'maxDate' | 'maxDetail' | 'activeStartDate'
    >;
    onChange(newValue?: Date): void;

    /**
     * Pass a function to determine if a certain day should be displayed as disabled.
     */
    tileDisabled?: TileDisabledFunc;
}

const DateInput = ({
    validDates,
    value,
    onChange,
    endAdornment,
    calendarProps,
    tileDisabled: tileDisabledProp,
    ...inputProps
}: DateInputProps) => {
    const intl = useIntl();

    const { isInMobileView } = useContext(LayoutContext);
    const [showDatePicker, setShowDatePicker] = useState(false);
    const [localValue, setLocalValue] = useState<Value>(value || null);

    const tileDisabledDefaultCallback = useCallback(
        (tileArgs: TileArgs) => {
            if (!validDates) {
                return false;
            }

            return isTileDisabledForDate(
                tileArgs,
                validDates,
                value ? [value] : []
            );
        },
        [validDates, value]
    );

    const tileDisabled = tileDisabledProp || tileDisabledDefaultCallback;

    const onApply = () => {
        onChange((localValue as Date | null) || undefined);
        setShowDatePicker(false);
    };

    const onClear = () => {
        setLocalValue(null);
        onChange(undefined);
        setShowDatePicker(false);
    };

    const dateInput = useMemo(
        () => (
            <Calendar
                {...calendarProps}
                showFixedNumberOfWeeks
                className="singleDatePicker"
                locale={intl.locale}
                onChange={setLocalValue}
                value={localValue}
                tileDisabled={tileDisabled}
                minDetail="decade"
                prevLabel={<ChevronLeftIcon />}
                nextLabel={<ChevronRightIcon />}
                prev2Label={null}
                next2Label={null}
            />
        ),
        [calendarProps, intl.locale, localValue, tileDisabled]
    );

    const formattedValue = value
        ? DateTime.fromJSDate(value).toFormat('dd.LL.yyyy')
        : null;

    return (
        <>
            <TooltipStateLess
                disabled={inputProps.disabled}
                showArrow
                modal
                mode="click"
                open={!isInMobileView && showDatePicker}
                onOpenChange={state => {
                    setShowDatePicker(state);
                }}
                fallbackPlacements={['bottom', 'top']}
                theme={styles.tooltipTheme}
                content={
                    <div className="dateInput">
                        {dateInput}
                        <div className={styles.calendarActions}>
                            <HStack justify="end">
                                <Button
                                    text
                                    onClick={onApply}
                                    disabled={
                                        !localValue || localValue === value
                                    }
                                >
                                    <FormattedMessage id="Library DateInput Apply" />
                                </Button>
                                <Button
                                    text
                                    secondary
                                    disabled={!value}
                                    onClick={onClear}
                                >
                                    <FormattedMessage id="Library DateInput Clear" />
                                </Button>
                            </HStack>
                        </div>
                    </div>
                }
            >
                <div className={styles.trigger}>
                    <BaseInput
                        {...inputProps}
                        value={formattedValue ?? ''}
                        error={
                            value
                                ? tileDisabled({
                                      date: value,
                                      view: 'month',
                                      activeStartDate: value,
                                  })
                                : undefined
                        }
                        endAdornment={
                            value ? (
                                <HStack align="center" gap="mini">
                                    <Button
                                        text
                                        secondary
                                        inline
                                        onClick={onClear}
                                    >
                                        <CloseIcon small bold />
                                    </Button>
                                    {endAdornment && (
                                        <>
                                            <div
                                                className={
                                                    styles.adornmentDivider
                                                }
                                            />
                                            {endAdornment}
                                        </>
                                    )}
                                </HStack>
                            ) : (
                                endAdornment
                            )
                        }
                    >
                        <div>{formattedValue ?? inputProps.placeholder}</div>
                    </BaseInput>
                </div>
            </TooltipStateLess>
            <SidePane
                hideOverflow
                side="bottom"
                open={isInMobileView && showDatePicker}
                onOpenChange={isOpen =>
                    isInMobileView ? setShowDatePicker(isOpen) : undefined
                }
                footer={
                    <HStack justify="spaceBetween" gap="small">
                        <Button text onClick={onApply} disabled={!localValue}>
                            <FormattedMessage id="Library DateInput Apply" />
                        </Button>
                        <Button
                            text
                            secondary
                            disabled={!value}
                            onClick={onClear}
                        >
                            <FormattedMessage id="Library DateInput Clear" />
                        </Button>
                    </HStack>
                }
            >
                <div className="dateInput">{dateInput}</div>
            </SidePane>
        </>
    );
};

export default memo(DateInput);
